import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchJSON } from 'api/fetch';
import { captureException } from 'fe-core/util/errorTracking';
import { RootState } from 'initializers/types';

import { PARENT_SLICE_NAME, SLICE_NAME } from './constants';
import {
  FetchWeatherForecastParams,
  WeatherForecast,
  WeatherForecastState,
} from './types';

const initialState: WeatherForecastState = {
  weatherForecasts: {},
  status: 'idle',
  error: undefined,
};

export const fetchWeatherForecast = createAsyncThunk<
  WeatherForecast[],
  FetchWeatherForecastParams
>(
  'weatherForecast/fetchWeatherForecast',
  async (params: FetchWeatherForecastParams) => {
    const response = await fetchJSON(
      // eslint-disable-next-line max-len
      `/schedule_builder/weather_forecast?end_date=${params.end_date}&start_date=${params.start_date}`
    );
    return response;
  }
);

const WeatherForecastSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    clearAllEntities: state => {
      state.weatherForecasts = {};
      state.status = 'idle';
      state.error = undefined;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchWeatherForecast.fulfilled, (state, action) => {
      if (!Array.isArray(action.payload)) {
        captureException(
          new Error('Weather forecast payload is not an array'),
          {
            extra: {
              payload: JSON.stringify(action.payload),
              payloadType: typeof action.payload,
            },
          }
        );
        state.status = 'failed';
        return;
      }

      state.weatherForecasts = action.payload.reduce(
        (acc: Record<string, WeatherForecast>, forecast: WeatherForecast) => {
          acc[forecast.date] = forecast;
          return acc;
        },
        {}
      );

      state.status = 'succeeded';
    });
    builder.addCase(fetchWeatherForecast.pending, state => {
      state.status = 'pending';
    });
    builder.addCase(fetchWeatherForecast.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to get weather forecast';
      state.status = 'failed';
    });
  },
});

export const selectWeatherForecastForDate = (state: RootState, date: string) =>
  state.get(PARENT_SLICE_NAME)?.[SLICE_NAME].weatherForecasts[date] ?? {};

export const selectWeatherForecastFetchIsPending = (state: RootState) =>
  state.get(PARENT_SLICE_NAME)?.[SLICE_NAME]?.status === 'pending';

export const { actions, reducer } = WeatherForecastSlice;
